import { AfterViewInit, ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';

import dayjs from 'dayjs';
import { startWith, takeUntil } from 'rxjs';

import { DefaultIcon, SDate } from '@summit/core';
import { DisplayType, SummitIdObject } from '@summit/models';

import { AsyncPipe } from '@angular/common';
import { BlurDirective, LayoutService } from '@summit/ui';
import { Button } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { FloatLabel } from 'primeng/floatlabel';
import { InputGroup } from 'primeng/inputgroup';
import { InputGroupAddon } from 'primeng/inputgroupaddon';
import { SummitDateControl } from '../../models/summit-date-control';
import { SFormErrorInputComponent } from '../s-form-error-input';

@Component({
  selector: 's-form-input-date',
  templateUrl: './s-form-input-date.component.html',
  standalone: true,
  imports: [
    AsyncPipe,
    SFormErrorInputComponent,
    FormsModule,
    BlurDirective,
    DatePickerModule,
    FloatLabel,
    Button,
    InputGroup,
    InputGroupAddon
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SFormInputDateComponent<T extends SummitIdObject>
  extends SummitDateControl<T>
  implements OnInit, AfterViewInit
{
  @Input() labelLocation: 'over' | 'in' | 'on' = 'over';
  @Input() defaultDay: Date;
  @Input() size: 'small' | 'large';

  @Input() showButtons = false;
  //@Input() shiftTZ: boolean;
  convertUtcToLocal: boolean;

  defaultIcon = DefaultIcon;
  private buffer?: string;

  constructor(protected layoutService: LayoutService) {
    super();
  }

  override ngOnInit() {
    super.ngOnInit();
    const attribute = this.schema ? this.schema?.refColumnList[this.fieldName ?? ''] : undefined;
    this.convertUtcToLocal =
      attribute?.displayType === DisplayType.Time ||
      attribute?.displayType === DisplayType.DateTimeLocal ||
      attribute?.displayType === DisplayType.DateTimeSecLocal;
    if (this.defaultDay) {
      this.updateDay(this.defaultDay);
    }
  }

  ngAfterViewInit() {
    const fieldName = this.fieldName?.toString();
    if (!fieldName) {
      console.error(`SFormInputDateComponent: Missing fieldName`);
      return;
    }
    //Conversion from Reactive Forms to PrimeNG Calendar
    if (this.form !== null && !this.form?.controls[this.fieldName ?? ''] && this.schema !== false) {
      console.error(`SFormInputDateComponent: No form control for ${this.fieldName}`);
      //console.log(this.form.controls);
      return;
    }
    this.form?.controls[this.fieldName ?? '']?.valueChanges
      .pipe(startWith(this.form.controls[this.fieldName ?? '']?.value), takeUntil(this.disposing$))
      .subscribe((value) => {
        let lclDate = this.convertUtcToLocal
          ? value
            ? SDate.tz(SDate.newUtc(value), dayjs.tz.guess())
            : undefined
          : value
            ? SDate.ShiftToTz(SDate.newUtc(value), dayjs.tz.guess())
            : undefined;
        //console.log(`${this.fieldName.toString()}: Converted to local time`, value, lclDate);
        if (!lclDate) {
          this.dateValue = undefined;
        } else if (!this.dateValue || !lclDate.isSame(this.dateValue)) {
          if (!this.leaveTimeAlone) lclDate = lclDate.startOf('day');
          //console.log(`${this.fieldName.toString()}: Control change: `, lclDate, this.dateValue);
          this.dateValue = lclDate.toDate(); //using value instead of lclDate to avoid conversion
        }
        this.cd.detectChanges();
      });
  }

  moveToToday() {
    this.updateDay(new Date());
  }

  moveBackDay() {
    // Enter key in input is triggering this so added a if to separate decision (see week range if needed)
    const currentValue = this.dateValue ? new Date(this.dateValue) : new Date();
    currentValue.setDate(currentValue.getDate() - 1);
    this.updateDay(currentValue);
  }

  moveForwardDay() {
    const currentValue = this.dateValue ? new Date(this.dateValue) : new Date();
    currentValue.setDate(currentValue.getDate() + 1);
    this.updateDay(currentValue);
  }

  updateDay(newDate: Date) {
    this.dateValue = newDate;
    const updatedDate = SDate.new(this.dateValue);
    const lclDate = SDate.ShiftTZToUTC(updatedDate);
    lclDate?.setHours(0, 0, 0, 0);
    this.form?.controls[this.fieldName ?? '']?.setValue(lclDate);
    this.DateChange.emit(updatedDate.startOf('day'));
  }

  onChange() {
    const fieldName = this.fieldName?.toString();
    if (!fieldName) return;
    if (!this.dateValue && this.buffer && this.buffer.length > 0) {
      const pattern = /^(\d{1,2})\D(\d{1,2})\D?(\d{2,4})?$/;
      const match = pattern.exec(this.buffer);
      if (match && match.length > 3) {
        const month = parseInt(match[1]);
        const day = parseInt(match[2]);
        const currentYear = new Date().getFullYear();
        let year = match[3] ? parseInt(match[3]) : currentYear;
        if (year < 100) {
          const century = currentYear - (currentYear % 100);
          //console.log('century', century);
          year += century;
        }
        this.dateValue = new Date(year, month - 1, day);
      }
    }
    this.buffer = undefined;
    if (this.dateValue === null || this.dateValue === undefined || (this.dateValue as unknown as string) === '') {
      this.form?.controls[this.fieldName ?? '']?.setValue(null);
      this.DateChange.emit(undefined);
    } else {
      //console.log('onChange start', this.dateValue);
      let updatedDate = SDate.new(this.dateValue);
      if (this.leaveTimeAlone) {
        //console.log('leave time alone');
        let currentValue = this.form?.controls[this.fieldName ?? '']?.value;
        if (SDate.isDayjs(currentValue)) {
          //console.log('currentValue is Dayjs', currentValue.format());
          if (this.convertUtcToLocal) {
            currentValue = SDate.tz(currentValue, dayjs.tz.guess());
            //console.log('currentValue is adjusted', currentValue.format());
          }
          //console.log('current value breakdown', currentValue.hour(), currentValue.minute(), currentValue.second());
          updatedDate = updatedDate
            .hour(currentValue.hour())
            .minute(currentValue.minute())
            .second(currentValue.second());
          //console.log('updated date sync', updatedDate.format());
        }
      }
      //console.log('onChange', updatedDate.format(), SDate.ShiftToTz(updatedDate, 'utc').format());
      const selDate = this.convertUtcToLocal ? SDate.tz(updatedDate, 'utc') : SDate.ShiftToTz(updatedDate, 'utc');
      //console.log('onChange setting value to', this.convertUtcToLocal, selDate.format());
      this.form?.controls[this.fieldName ?? '']?.setValue(selDate);
      this.DateChange.emit(updatedDate);
    }
    this.form?.controls[this.fieldName ?? '']?.markAsTouched();
    this.form?.controls[this.fieldName ?? '']?.markAsDirty();
    //}
  }

  onInput($event) {
    this.buffer ??= '';
    if ($event.inputType === 'deleteContentBackward') {
      this.buffer = this.buffer.slice(0, -1);
    } else {
      this.buffer += $event.data;
    }
    //console.log('buffer', this.buffer, $event);
  }

  get value(): Date {
    return this.form?.controls[this.fieldName ?? '']?.value; //.clone().startOf('day').toDate();
  }

  getObject(): dayjs.Dayjs {
    return this.value ? SDate.newUtc(this.value) : SDate.newUtc();
  }
}
